/**
 * Centralized plan descriptions and features
 * Shared between ChoosePlans.js and ServicePlans.js
 *
 * This ensures consistent marketing messaging across all plan displays
 * while allowing each component to maintain its unique UI/UX.
 */

export const PLAN_FEATURE_KEYS = {
  individual: [
    "servicePlans.feature.covers1User",
    "servicePlans.feature.realtimeScam",
    "servicePlans.feature.screenshotScan",
    "servicePlans.feature.phoneVerify",
    "servicePlans.feature.textConvo",
    "servicePlans.feature.bizCheck",
    "servicePlans.feature.simPhishing",
    "servicePlans.feature.trendingAlerts",
    "servicePlans.feature.trainings",
    "servicePlans.feature.monthlyReports",
    "servicePlans.feature.freeTrial",
  ],

  family: [
    "servicePlans.feature.covers3Users",
    "servicePlans.feature.everythingIndividual",
    "servicePlans.feature.advancedSimCustom",
    "servicePlans.feature.advancedThreatSim",
    "servicePlans.feature.realWorldScenarios",
    "servicePlans.feature.moreUsage",
    "servicePlans.feature.weeklyReports",
    "servicePlans.feature.referralBonus",
  ],

  extended_family: [
    "servicePlans.feature.covers6Users",
    "servicePlans.feature.everythingFamily",
    "servicePlans.feature.myScamShield",
    "servicePlans.feature.highestUsage",
    "servicePlans.feature.prioritySupport",
    "servicePlans.feature.indepthReports",
    "servicePlans.feature.earlyAccess",
  ],

  "smb-10": [
    "servicePlans.feature.coversUpTo10Employees",
    "servicePlans.feature.realtimeScam",
    "servicePlans.feature.screenshotScan",
    "servicePlans.feature.simPhishing",
    "servicePlans.feature.advancedThreatSim",
    "servicePlans.feature.trainings",
    "servicePlans.feature.adminDashboard",
    "servicePlans.feature.teamReports",
    "servicePlans.feature.prioritySupport",
    "servicePlans.feature.freeTrial14",
  ],

  "smb-25": [
    "servicePlans.feature.coversUpTo25Employees",
    "servicePlans.feature.realtimeScam",
    "servicePlans.feature.screenshotScan",
    "servicePlans.feature.simPhishing",
    "servicePlans.feature.advancedThreatSim",
    "servicePlans.feature.trainings",
    "servicePlans.feature.adminDashboard",
    "servicePlans.feature.teamReports",
    "servicePlans.feature.auditLogs",
    "servicePlans.feature.prioritySupport",
    "servicePlans.feature.freeTrial14",
  ],

  "smb-50": [
    "servicePlans.feature.coversUpTo50Employees",
    "servicePlans.feature.everythingTeam",
    "servicePlans.feature.advancedThreatSim",
    "servicePlans.feature.adminDashboard",
    "servicePlans.feature.teamReports",
    "servicePlans.feature.auditLogs",
    "servicePlans.feature.indepthReports",
    "servicePlans.feature.prioritySupport",
    "servicePlans.feature.freeTrial14",
  ],

  "smb-75": [
    "servicePlans.feature.coversUpTo75Employees",
    "servicePlans.feature.everythingTeam",
    "servicePlans.feature.advancedThreatSim",
    "servicePlans.feature.adminDashboard",
    "servicePlans.feature.teamReports",
    "servicePlans.feature.auditLogs",
    "servicePlans.feature.indepthReports",
    "servicePlans.feature.prioritySupport",
    "servicePlans.feature.freeTrial14",
  ],

  "smb-100": [
    "servicePlans.feature.coversUpTo100Employees",
    "servicePlans.feature.everythingTeam",
    "servicePlans.feature.advancedThreatSim",
    "servicePlans.feature.adminDashboard",
    "servicePlans.feature.teamReports",
    "servicePlans.feature.auditLogs",
    "servicePlans.feature.indepthReports",
    "servicePlans.feature.prioritySupport",
    "servicePlans.feature.freeTrial14",
  ],
};

/**
 * Get features for a specific plan by name or ID
 * @param {string|number} planIdentifier - Plan name (e.g., 'individual') or plan_id (1, 2, 3)
 * @param {Function} [t] - Optional translation function; if omitted returns raw i18n keys
 * @returns {string[]} Array of translated feature strings (or keys if t not provided)
 */
export const getPlanFeatures = (planIdentifier, t) => {
  // If it's a plan_id (number), map to plan name
  const planNameMap = {
    1: "individual",
    2: "family",
    3: "extended_family",
  };

  const planName =
    typeof planIdentifier === "number"
      ? planNameMap[planIdentifier]
      : planIdentifier.toLowerCase().replace(/\s+/g, "_");

  const keys = PLAN_FEATURE_KEYS[planName] || [];
  return t ? keys.map((k) => t(k)) : keys;
};

/**
 * Get display title for a plan
 * @param {string} planName - Internal plan name (e.g., 'extended_family')
 * @returns {string} Display name (e.g., 'Extended Family')
 */
export const getPlanDisplayName = (planName) => {
  const displayNames = {
    individual: "Individual",
    family: "Family",
    extended_family: "Extended Family",
  };

  return displayNames[planName] || planName;
};
